/**
 * /sign-in — Story 1b.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthCard } from '../../components/auth/auth-card.tsx';
import { Button, Input } from '@/components/ui';
import { authApi } from '../../state/auth/api.ts';

const schema = z.object({
  email: z.string().email('enter a valid email'),
  password: z.string().min(1, 'password is required'),
});

type FormValues = z.infer<typeof schema>;

/** Validates that a return_to value is a same-origin relative path per J-X5 § Substrate compliance. */
function isValidReturnTo(value: string): boolean {
  // Must start with / but NOT // (which would be protocol-relative open redirect).
  // Regex ^\/[^\/] matches /foo but not //evil.com or https://evil.com.
  return /^\/[^/]/.test(value);
}

export function SignInPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    // NOTE: authApi.signIn uses bare fetch intentionally.
    // Using apiClient here would create a 401-loop on the auth endpoint itself.
    const res = await authApi.signIn(values);
    if (!res.ok) {
      // Anti-enumeration: render every credential-failure shape identically.
      if (res.error.code === 'rate_limited') {
        setServerError(
          'Too many failed attempts. Please wait 15 minutes and try again.',
        );
      } else {
        setServerError('Email or password is incorrect.');
      }
      return;
    }
    // J-X5 § Path B: consume ?return_to= if present and valid same-origin path.
    // Validate with isValidReturnTo to prevent open-redirect attacks.
    const params = new URLSearchParams(location.search);
    const returnTo = params.get('return_to') ?? '';
    const destination = isValidReturnTo(returnTo) ? returnTo : '/post-sign-in';
    navigate(destination, { replace: true });
  });

  return (
    <AuthCard
      eyebrow="Sign in"
      title="Welcome back."
      footer={
        <>
          Don&apos;t have an account?{' '}
          <Link className="text-ink underline hover:text-accent" to="/sign-up">
            Sign up
          </Link>
          .
        </>
      }
    >
      <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
        <Input label="Work email" id="si-email" error={errors.email?.message} type="email" autoComplete="email" {...register('email')} />
        <Input label="Password" id="si-password" error={errors.password?.message} type="password" autoComplete="current-password" {...register('password')} />
        {serverError ? (
          <p className="font-sans text-small text-error" role="alert">
            {serverError}
          </p>
        ) : null}
        <Button variant="primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
        <p className="font-sans text-small text-muted">
          <Link className="text-ink underline hover:text-accent" to="/forgot-password">
            Forgot password?
          </Link>
        </p>
      </form>
    </AuthCard>
  );
}
